import { tr, usePartnerTranslation } from "../../../lib/language";
/**
 * Location search components powered by the public Photon API
 * (https://photon.komoot.io/) — built on OpenStreetMap data.
 * No API key required. Used under their fair-use policy with debounced
 * queries and an "Powered by OpenStreetMap" credit shown to users.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, X, Search, Loader2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { findRestrictedCountryName } from "../../../lib/constants";
import {
  CONTACT_INFO_ERROR,
  containsContactInfo,
} from "../../../lib/productTextSafety";

const PHOTON_URL = "https://photon.komoot.io/api/";
const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 6;
const CITY_PLACE_TYPES = new Set([
  "city",
  "town",
  "village",
  "municipality",
]);

/* ---------- Helpers ---------- */
function formatFeature(feature) {
  const p = feature?.properties || {};
  const g = feature?.geometry || {};
  // Photon returns coordinates as [lon, lat]
  const coords =
    Array.isArray(g.coordinates) && g.coordinates.length === 2
      ? { lat: g.coordinates[1], lng: g.coordinates[0] }
      : null;
  const uniqueParts = (parts) => {
    const seen = new Set();
    return parts.filter((part) => {
      const value = String(part || "").trim();
      if (!value) return false;
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  // Title: name (or composed street/locality fallback)
  const title = p.name || p.city || p.locality || p.county || p.street || p.country || "Location";
  // Subtitle path: state → country, joined and de-duped against the title
  const parts = uniqueParts([p.state, p.country].filter((x) => x !== title));
  const subtitle = parts.join(", ");
  // Tag value: short, friendly, just the name
  const tag = title;
  // Single-input value: longer, with locality
  const full = p.name
    ? [p.name, p.street, p.city, p.country].filter(Boolean).join(", ")
    : [p.street, p.locality, p.city, p.country].filter(Boolean).join(", ");
  const cityFull = uniqueParts([title, p.state, p.country]).join(", ");
  return {
    title,
    subtitle,
    tag,
    full,
    cityFull,
    country: p.country || "",
    coords,
    placeType: p.osm_value || p.type || "",
    raw: p,
  };
}

function useLocationSearch(query, { cityOnly = false } = {}) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    const q = (query || "").trim();
    if (q.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const id = ++reqIdRef.current;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=${RESULT_LIMIT}&lang=en`,
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (id !== reqIdRef.current) return;
        const feats = (json.features || [])
          .map(formatFeature)
          .filter((feature) => !cityOnly || CITY_PLACE_TYPES.has(feature.placeType))
          .filter((feature) => !findRestrictedCountryName(`${feature.country} ${feature.full}`));
        setResults(feats);
      } catch (e) {
        if (id !== reqIdRef.current) return;
        setError(e?.message || tr("Search failed"));
        setResults([]);
      } finally {
        if (id === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, cityOnly]);

  return { results, loading, error };
}

/* ---------- Suggestion popover ---------- */
const SuggestionsPopover = ({
  open,
  query,
  results,
  loading,
  highlight,
  onPick,
  onMouseEnterRow,
  testid,
  allowFreeText = true,
}) => {
  usePartnerTranslation();
  if (!open) return null;
  const showEmpty = !loading && results.length === 0 && query.length >= 2;
  return (
    <div
      data-testid={testid}
      role="listbox"
      className="absolute start-0 end-0 top-full z-30 mt-1 max-h-[280px] overflow-y-auto rounded-lg border border-[#EAECEF] bg-white shadow-lg"
    >
      {loading && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-[#6B7280]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />{tr("Searching…")}</div>
      )}
      {showEmpty && (
        <div className="px-3 py-3 text-xs text-[#6B7280]">
          {allowFreeText ? (
            <>{tr("No matches. Press ")}<strong>{tr("Enter")}</strong>{tr(" to add \"")}{query}{tr("\" as-is.")}</>
          ) : (
            tr("No city matches. Try a nearby city or another spelling.")
          )}
        </div>
      )}
      {results.map((it, i) => {
        const active = i === highlight;
        return (
          <button
            type="button"
            key={`${it.title}-${i}`}
            data-testid={testid ? `${testid}-row-${i}` : undefined}
            onMouseEnter={() => onMouseEnterRow?.(i)}
            onMouseDown={(e) => {
              e.preventDefault(); // don't blur the input
              onPick(it);
            }}
            className={cn(
              "flex w-full items-start gap-2 px-3 py-2 text-start transition-colors",
              active ? "bg-[#FFEFEF]" : "hover:bg-[#FAFAFB]",
            )}
          >
            <MapPin
              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#6B7280]"
              strokeWidth={1.8}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-[#0F1115]">
                {it.title}
              </span>
              {it.subtitle && (
                <span className="block truncate text-xs text-[#6B7280]">
                  {it.subtitle}
                </span>
              )}
            </span>
          </button>
        );
      })}
      {(results.length > 0 || loading) && (
        <p className="border-t border-[#F0F2F5] px-3 py-1.5 text-[10px] text-[#9BA1AB]">
          Powered by OpenStreetMap
        </p>
      )}
    </div>
  );
};

/* ---------- LocationTagInput (replaces TagInput for visit_locations) ---------- */
export const LocationTagInput = ({
  values = [],
  onChange,
  onPicksChange,
  placeholder,
  testid,
  maxItems,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [restrictedError, setRestrictedError] = useState("");
  const contactError =
    enforceNoContactInfo && (containsContactInfo(query) || values.some(containsContactInfo));
  const { results, loading } = useLocationSearch(query);

  useEffect(() => setHighlight(0), [results.length]);

  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));

  const tryAdd = (next, picked = null) => {
    const v = (next || "").trim();
    if (!v) return;
    if (findRestrictedCountryName(`${picked?.country || ""} ${picked?.full || ""} ${v}`)) {
      setRestrictedError(tr("This country is not supported for Voocly supplier products."));
      return;
    }
    if (enforceNoContactInfo && containsContactInfo(v)) return;
    if (maxItems && values.length >= maxItems) return;
    if (values.includes(v)) return;
    setRestrictedError("");
    onChange([...values, v]);
    onPicksChange?.(picked);
  };

  const pickResult = (item) => {
    tryAdd(item.tag, {
      label: item.tag,
      full: item.full,
      country: item.country,
      coords: item.coords,
    });
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (results.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.min(results.length - 1, h + 1));
      }
    } else if (e.key === "ArrowUp") {
      if (results.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && results[highlight]) {
        pickResult(results[highlight]);
      } else if (query.trim()) {
        tryAdd(query.trim(), null);
        setQuery("");
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (
      e.key === "Backspace" &&
      !query &&
      values.length
    ) {
      remove(values.length - 1);
    }
  };

  return (
    <div className="relative">
      <div
        onClick={() => inputRef.current?.focus()}
        data-testid={testid}
        className={cn(
          // `!` is required: .humrahe-input lives in @layer utilities after Tailwind's own
          // utilities, so its display:block / height:44px would otherwise win and wrapped
          // tags would spill outside the box over the content below.
          "humrahe-input !flex !h-auto min-h-[44px] flex-wrap items-center gap-1.5 py-2",
          contactError && "border-[#9B0000] focus-within:border-[#9B0000]",
        )}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            data-testid={testid ? `${testid}-chip-${i}` : undefined}
            className="inline-flex h-7 items-center gap-1 rounded-full bg-[#F3EEEE] px-2.5 text-xs font-medium text-[#0F1115]"
          >
            <MapPin className="h-3 w-3 text-[#6B7280]" strokeWidth={1.8} />
            {v}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(i);
              }}
              aria-label={tr("Remove {{value0}}", { value0: v })}
              className="text-[#6B7280] hover:text-[#9B0000]"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 100)}
          onKeyDown={onKeyDown}
          placeholder={values.length ? "" : placeholder || tr("Type a city or place…")}
          className="flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-[#B6BAC2]"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </div>
      <SuggestionsPopover
        open={open && (loading || results.length > 0 || query.trim().length >= 2)}
        query={query}
        results={results}
        loading={loading}
        highlight={highlight}
        onPick={pickResult}
        onMouseEnterRow={setHighlight}
        testid={testid ? `${testid}-suggestions` : undefined}
      />
      {restrictedError && (
        <p className="mt-1 text-xs font-medium text-[#9B0000]">{restrictedError}</p>
      )}
      {contactError && (
        <p className="mt-1 text-xs font-medium text-[#9B0000]">{CONTACT_INFO_ERROR}</p>
      )}
    </div>
  );
};

/* ---------- LocationTextInput (single-value text + autocomplete) ---------- */
export const LocationTextInput = ({
  value,
  onChange,
  onPick: onPickProp,
  placeholder,
  testid,
  error,
  enforceNoContactInfo = false,
}) => {
  usePartnerTranslation();
  const inputRef = useRef(null);
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const { results, loading } = useLocationSearch(query);
  const contactError = enforceNoContactInfo && containsContactInfo(query);
  const displayError = error || (contactError ? CONTACT_INFO_ERROR : "");

  // Sync external value updates (e.g. duplicate hydration)
  useEffect(() => setQuery(value || ""), [value]);
  useEffect(() => setHighlight(0), [results.length]);

  const pick = (item) => {
    setQuery(item.full);
    // Fire ONE parent callback so caller can batch value + coords into a
    // single state update. This avoids the stale-closure bug where two
    // back-to-back state setters overwrite each other.
    if (onPickProp) {
      onPickProp(item);
    } else {
      // Fallback: update value only.
      onChange(item.full);
    }
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (results.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.min(results.length - 1, h + 1));
      }
    } else if (e.key === "ArrowUp") {
      if (results.length > 0) {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
      }
    } else if (e.key === "Enter") {
      if (open && results[highlight]) {
        e.preventDefault();
        pick(results[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9BA1AB]"
          strokeWidth={1.8}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            const next = e.target.value;
            setQuery(next);
            // Free-typing only updates the value. We deliberately keep the
            // last-picked coordinates around so minor edits ("Colosseum,
            // Rome — north entrance") don't make the map disappear. Coords
            // are refreshed the next time the supplier picks a suggestion.
            onChange(next);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 100)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={testid}
          className={cn(
            "humrahe-input !ps-10",
            displayError && "border-[#9B0000] focus:border-[#9B0000]",
          )}
        />
      </div>
      <SuggestionsPopover
        open={open && (loading || results.length > 0 || query.trim().length >= 2)}
        query={query}
        results={results}
        loading={loading}
        highlight={highlight}
        onPick={pick}
        onMouseEnterRow={setHighlight}
        testid={testid ? `${testid}-suggestions` : undefined}
      />
      {contactError && !error && (
        <p className="mt-1 text-xs font-medium text-[#9B0000]">{CONTACT_INFO_ERROR}</p>
      )}
    </div>
  );
};

/* ---------- CityTextInput (single selected city from OpenStreetMap data) ---------- */
export const CityTextInput = ({
  value,
  onChange,
  placeholder,
  testid,
  error,
}) => {
  usePartnerTranslation();
  const inputRef = useRef(null);
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [selectionError, setSelectionError] = useState("");
  const { results, loading } = useLocationSearch(query, { cityOnly: true });
  const displayError = error || selectionError;

  useEffect(() => setQuery(value || ""), [value]);
  useEffect(() => setHighlight(0), [results.length]);

  const pick = (item) => {
    const next = item.cityFull || item.title;
    setQuery(next);
    setSelectionError("");
    onChange(next);
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      setHighlight((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp" && results.length > 0) {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && results[highlight]) pick(results[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9BA1AB]"
          strokeWidth={1.8}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectionError(tr("Pick a city from the list."));
            onChange("");
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 100)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-testid={testid}
          className={cn(
            "humrahe-input !ps-10",
            displayError && "border-[#9B0000] focus:border-[#9B0000]",
          )}
        />
      </div>
      <SuggestionsPopover
        open={open && (loading || results.length > 0 || query.trim().length >= 2)}
        query={query}
        results={results}
        loading={loading}
        highlight={highlight}
        onPick={pick}
        onMouseEnterRow={setHighlight}
        testid={testid ? `${testid}-suggestions` : undefined}
        allowFreeText={false}
      />
      {selectionError && !error && (
        <p className="mt-1 text-xs font-medium text-[#9B0000]">{selectionError}</p>
      )}
    </div>
  );
};
